import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FlatList, StatusBar } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  getLatestConversationsRequest,
  openConversationRequest,
} from "@microdoing/shared/services/message/actions";
import {
  getConversationId,
  getMessageRequesting,
  getSortedConversations,
} from "@microdoing/shared/services/message/selectors";
import { clearMessageNotifications } from "@microdoing/shared/services/notification/actions";
import {
  changeSearchType,
  onChangeSearch,
} from "@microdoing/shared/services/search/actions";
import {
  getUserFirstName,
  getUserId,
} from "@microdoing/shared/services/user/selectors";
import i18n from "@microdoing/shared/i18n";
import SearchButton from "../SearchScreen/SearchButton";
import NotificationsModal from "../ActionScreen/NotificationsModal";
import { Box, MainText, PressableOpacity } from "app/components/common";
import NewMessageIcon from "./NewMessageIcon";
import { getNewMessageNotifications } from "@microdoing/shared/services/notification/selectors";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import hasNotificationPermission from "@microdoing/shared/utils/hasNotificationPermission";
import { cancelAllNotifications } from "@microdoing/shared/services/notifService";
import hasTitleConversationGroup from "app/utils/hasTitleConversationGroup";
import theme from "app/Theme/Theme";
import normalize from "app/utils/normalize";
import { useNavigation } from "@react-navigation/native";
import HeaderFullScreen from "app/components/common/HeaderFullScreen";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import ChatScreen from "./ChatScreen/ChatScreen";
import AlgoliaSearch from "../SearchScreen/AlgoliaSearch";
import SearchHits from "./SearchHits";
import { breadCrumbsConversationScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import {
  getTeam,
  getTeamConversation,
  getUserCampaignTeamSize,
} from "@microdoing/shared/services/userCampaign/selectors";
import ConversationRow from "./ConversationRow";
import { SafeAreaView } from "react-native-safe-area-context";
import AlgoliaSearchButton from "../SearchScreen/AlgoliaSearchButton";

const MessageScreen = () => {
  const [isNotificationModalVisible, setIsNotificationModalVisible] = useState(
    false,
  );
  const [isSearching, setIsSearching] = useState(false);

  const team = useSelector(getTeam);
  const teamConversationId = team.conversationId || "oldTeam";
  const teamConversation = useSelector(getTeamConversation);
  const campaignTeamSize = useSelector(getUserCampaignTeamSize);
  const requesting = useSelector(getMessageRequesting);
  const firstName = useSelector(getUserFirstName);
  const newMessageNotifications = useSelector(getNewMessageNotifications);
  const language = useSelector(getLanguage);
  const userId = useSelector(getUserId);
  const conversations = useSelector(getSortedConversations);
  const conversationId = useSelector(getConversationId);

  const hasTeam = useMemo(() => campaignTeamSize > 1, [campaignTeamSize]);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const notificationPermission = useCallback(async () => {
    const enabled = await hasNotificationPermission();

    if (!enabled) {
      await cancelAllNotifications();
      setTimeout(() => setIsNotificationModalVisible(true), 150);
    }
  }, []);

  useEffect(() => {
    if (newMessageNotifications) {
      dispatch(clearMessageNotifications());
    }
    dispatch(getLatestConversationsRequest({ userId: userId }));
    notificationPermission();
  }, [notificationPermission]);

  useEffect(() => {
    if (navigation.isFocused() && newMessageNotifications) {
      if (newMessageNotifications) {
        dispatch(clearMessageNotifications());
      }
    }
  }, [newMessageNotifications]);

  useEffect(() => {
    if (isWebDesktop && conversations?.length > 0 && !conversationId) {
      const conversation = conversations?.[0] || {};
      const { isGroup, _id } = conversation;

      const conversationData = Object.keys(conversation?.data)
        .map(el => {
          if (!isGroup && el !== userId) {
            return {
              _id: el,
              name: conversation?.data?.[el]?.name,
              picture: conversation?.data?.[el]?.picture,
            };
          }
          return undefined;
        })
        .filter(el => el !== undefined);
      onChatOpen(conversationData, _id, "");
    }
  }, []);

  const keyExtractor = (item, index) => {
    return item._id;
  };

  const onChatOpen = useCallback(
    (users, conversationId, conversationIdGroup) => {
      const receivers = users.map(el => {
        return { _id: el._id, name: el.name, picture: el.picture };
      });

      dispatch(
        openConversationRequest({
          receivers,
          conversationId,
          name: conversationIdGroup ? team.name : "",
        }),
      );
    },
    [],
  );

  const searchConversation = () => {
    if (!isWebDesktop) {
      navigation.navigate("NewConversation");
    } else {
      setIsSearching(true);
      dispatch(changeSearchType("message-user"));
    }
  };

  const onResetSearch = () => {
    setIsSearching(false);
    dispatch(onChangeSearch(""));
  };

  const onRefresh = () => {
    dispatch(getLatestConversationsRequest({ userId: userId }));
  };

  const triggerNotificationModal = () => {
    setIsNotificationModalVisible(false);
  };

  const displayTeamConversation = useCallback(() => {
    const { users } = team;
    const {
      createdAt = "",
      text = "",
      seen = true,
      seenBy = [],
      user = {},
    } = teamConversation?.latestMessage;

    const conversation =
      !seen &&
      teamConversation?.latestMessage?.user?._id !== userId &&
      !seenBy?.[userId];

    return (
      <Box marginTop="xxs">
        <ConversationRow
          conversationData={users}
          conversationItemId={teamConversationId}
          conversationIdGroup={teamConversationId}
          conversationName={team.name || ""}
          isNotSeen={!!conversation}
          user={user}
          createdAt={createdAt}
          text={text}
          openConversation={onChatOpen}
        />
        <Box marginTop="sm" borderColor="neutral.100" borderTopWidth={2} />
      </Box>
    );
  }, [team, onChatOpen, conversations]);

  const renderUserList = data => {
    const { latestMessage, participants, isGroup } = data.item;
    const conversationData = Object.keys(data.item.data)
      .map(el => {
        if (!isGroup && el !== userId) {
          return {
            _id: el,
            name: data?.item?.data?.[el]?.name,
            picture: data?.item?.data?.[el]?.picture,
          };
        } else if (isGroup) {
          return {
            _id: el,
            name: data?.item?.data?.[el]?.name,
            picture: data?.item?.data?.[el]?.picture,
          };
        }
        return undefined;
      })
      .filter(el => el !== undefined);
    // bug with an account send to himself
    if (conversationData.length === 0) return null;
    const conversationName = hasTitleConversationGroup(
      data.item,
      data.item.name,
      conversationData[0]?.name,
    ); // a transformer en useCallback lorsque transformation en tsx functionnal component
    const { user, text, createdAt, seen, seenBy } = latestMessage;

    const isNotSeen = !seen && !seenBy?.[userId] && user._id !== userId;

    if (team.conversationId === data?.item?._id) {
      return;
    }

    return (
      <ConversationRow
        conversationData={conversationData}
        conversationItemId={data?.item?._id}
        conversationIdGroup=""
        conversationName={conversationName}
        isNotSeen={isNotSeen}
        createdAt={createdAt}
        user={user}
        text={text}
        openConversation={onChatOpen}
      />
    );
  };

  const renderConversations = () => {
    return (
      <>
        {!isWebDesktop && (
          <>
            <NotificationsModal
              isVisible={isNotificationModalVisible}
              firstName={firstName}
              triggerModal={triggerNotificationModal}
            />
            <HeaderFullScreen
              title={i18n.t("messages")}
              leftIcon={
                <ArrowLeft
                  color={theme.colors["neutral.900"]}
                  height={normalize(30)}
                  width={normalize(30)}
                />
              }
              rightIcon={<NewMessageIcon color={theme.colors["brand.500"]} />}
              callbackOnPressRight={searchConversation}
            />
          </>
        )}

        <AlgoliaSearch>
          <Box
            flex={1}
            width="100%"
            borderWidth={isWebDesktop ? 0 : 1}
            borderColor="neutral.100"
            borderStyle="solid"
            borderRadius={16}
            backgroundColor="neutral.white"
          >
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              paddingHorizontal="m"
            >
              <Box
                backgroundColor="neutral.white"
                alignItems="center"
                justifyContent="center"
                flex={1}
                marginTop="sm"
                marginBottom="s"
              >
                {isWebDesktop ? (
                  <AlgoliaSearchButton
                    hasClearButton={isSearching}
                    boxStyle={styles.searchButton}
                    onPress={searchConversation}
                    onClickOutside={onResetSearch}
                    testID={"search-button-message-screen"}
                  />
                ) : (
                  <SearchButton
                    hasClearButton={isSearching}
                    boxStyle={styles.searchButton}
                    onPress={searchConversation}
                    onClickOutside={onResetSearch}
                    testID={"search-button-message-screen"}
                  />
                )}
              </Box>
              {isWebDesktop && (
                <PressableOpacity
                  onPress={searchConversation}
                  style={{
                    marginLeft: theme.spacing.xm,
                    marginTop: theme.spacing.sm,
                    marginBottom: theme.spacing.s,
                  }}
                >
                  <NewMessageIcon color={theme.colors["brand.500"]} />
                </PressableOpacity>
              )}
            </Box>
            {!!isSearching ? (
              <Box flex={1}>
                <SearchHits onFinish={onResetSearch} />
              </Box>
            ) : (
              <Box flex={1}>
                {displayTeamConversation()}
                <FlatList
                  initialNumToRender={10}
                  onRefresh={onRefresh}
                  refreshing={requesting}
                  data={conversations}
                  extraData={language}
                  renderItem={renderUserList}
                  keyExtractor={keyExtractor}
                  contentContainerStyle={{
                    paddingTop: hasTeam ? theme.spacing.xm : 0,
                  }}
                  showsVerticalScrollIndicator={false}
                  keyboardShouldPersistTaps="always"
                />
              </Box>
            )}
          </Box>
        </AlgoliaSearch>
      </>
    );
  };

  return isWebDesktop ? (
    <WebDesktopView breadcrumbsPath={breadCrumbsConversationScreen}>
      <Box flex={1} flexDirection="row" justifyContent="space-between">
        <Box
          width="40%"
          backgroundColor="neutral.white"
          borderRadius={isWebDesktop ? 0 : 12}
          paddingVertical={isWebDesktop ? "null" : "sm"}
          testID="conversation-screen"
        >
          {renderConversations()}
        </Box>
        <Box width="57%">
          <ChatScreen />
        </Box>
      </Box>
    </WebDesktopView>
  ) : (
    <SafeAreaView style={{ flex: 1 }} edges={["top"]}>
      <StatusBar animated backgroundColor={theme.colors["neutral.75"]} />
      <Box
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        testID="conversation-screen"
        backgroundColor="neutral.75"
      >
        {renderConversations()}
      </Box>
    </SafeAreaView>
  );
};

const styles = {
  userRow: {
    alignSelf: "center",
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.m,
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  searchButton: {
    height: normalize(40),
  },
};

export default MessageScreen;
